import React, { useState, useEffect } from 'react';
import { X, Package, AlertTriangle, Plus, Minus, Building2 } from 'lucide-react';
import { toast } from 'react-toastify';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { getBranches, getBranchMode } from '../api/branches';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';

const StockAdjustmentModal = ({ isOpen, onClose, product, onSuccess }) => {
  const { user } = useAuth();
  const [selectedBranchId, setSelectedBranchId] = useState('');
  const [branches, setBranches] = useState([]);
  const [isSingleBranch, setIsSingleBranch] = useState(true);
  const [newQuantity, setNewQuantity] = useState(0);
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingBranches, setLoadingBranches] = useState(false);

  const canSelectBranch = ['super_admin', 'system_admin'].includes(user?.role);

  useEffect(() => {
    if (isOpen && product) {
      fetchBranchesAndMode();
    }
  }, [isOpen, product]);

  useEffect(() => {
    // Update current stock when branch selection changes
    if (selectedBranchId && product?.branch_stocks) {
      const branchStock = product.branch_stocks.find(b => b.branch_id === parseInt(selectedBranchId));
      setNewQuantity(branchStock?.stock_quantity || 0);
    } else {
      setNewQuantity(product?.global_stock || product?.stock_quantity || 0);
    }
  }, [selectedBranchId, product]);

  const fetchBranchesAndMode = async () => {
    try {
      setLoadingBranches(true);
      const [modeRes, branchesRes] = await Promise.all([
        getBranchMode().catch(() => ({ data: { singleBranchMode: true } })),
        getBranches({ isActive: true }).catch(() => ({ data: [] }))
      ]);
      
      // Response is { success: true, data: { singleBranchMode: boolean } }
      const singleBranchMode = modeRes?.data?.singleBranchMode ?? modeRes?.singleBranchMode ?? true;
      setIsSingleBranch(singleBranchMode);
      setBranches(branchesRes?.data || branchesRes || []);
      
      // Set initial stock based on mode
      if (singleBranchMode) {
        setNewQuantity(product?.stock_quantity || 0);
      } else {
        setNewQuantity(product?.global_stock || 0);
      }
    } catch (error) {
      console.error('Failed to fetch branches:', error);
    } finally {
      setLoadingBranches(false);
    }
  };

  if (!isOpen || !product) return null;

  const getCurrentStock = () => {
    if (selectedBranchId && product.branch_stocks) {
      const branchStock = product.branch_stocks.find(b => b.branch_id === parseInt(selectedBranchId));
      return branchStock?.stock_quantity || 0;
    }
    return product.global_stock || product.stock_quantity || 0;
  };

  const currentStock = getCurrentStock();
  const difference = newQuantity - currentStock;

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (newQuantity < 0) {
      toast.error('Quantity cannot be negative');
      return;
    }

    if (!reason.trim()) {
      toast.error('Please provide a reason for the adjustment');
      return;
    }

    if (!isSingleBranch && canSelectBranch && !selectedBranchId) {
      toast.error('Please select a branch');
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem('accessToken');
      
      if (selectedBranchId) {
        // Branch-specific adjustment
        await axios.patch(
          `${API_URL}/branches/${selectedBranchId}/inventory/${product.id}/adjust`,
          { newQuantity: parseInt(newQuantity), reason: reason.trim() },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        // Legacy global adjustment (single branch mode)
        await axios.patch(
          `${API_URL}/products/${product.id}/adjust-stock`,
          { newQuantity: parseInt(newQuantity), reason: reason.trim() },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }

      toast.success('Stock adjusted successfully');
      onSuccess?.();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.error || error.response?.data?.message || 'Failed to adjust stock');
    } finally {
      setLoading(false);
    }
  };

  const quickAdjust = (amount) => {
    const newVal = Math.max(0, newQuantity + amount);
    setNewQuantity(newVal);
  };

  const selectedBranchName = selectedBranchId 
    ? branches.find(b => b.id === parseInt(selectedBranchId))?.name 
    : 'All Branches';

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Package className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-primary-black">Adjust Stock</h2>
                <p className="text-sm text-secondary-gray">{product.name}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-secondary-offWhite rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Branch Selection (for super_admin/system_admin in multi-branch mode) */}
            {!isSingleBranch && canSelectBranch && (
              <div>
                <label className="block text-sm font-medium text-primary-black mb-2 flex items-center gap-2">
                  <Building2 className="w-4 h-4" />
                  Select Branch <span className="text-red-500">*</span>
                </label>
                <select
                  value={selectedBranchId}
                  onChange={(e) => setSelectedBranchId(e.target.value)}
                  className="w-full px-4 py-2 border border-secondary-lightGray rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-gold"
                  disabled={loadingBranches}
                >
                  <option value="">-- Select a branch --</option>
                  {branches.map(branch => {
                    const branchStock = product.branch_stocks?.find(b => b.branch_id === branch.id);
                    return (
                      <option key={branch.id} value={branch.id}>
                        {branch.name} (Current: {branchStock?.stock_quantity || 0})
                      </option>
                    );
                  })}
                </select>
              </div>
            )}

            {/* Current Stock Display */}
            <div className="bg-secondary-offWhite rounded-lg p-4">
              <div className="flex items-center justify-between">
                <span className="text-secondary-gray">
                  Current Stock {selectedBranchId ? `(${selectedBranchName})` : ''}
                </span>
                <span className="text-xl font-bold text-primary-black">
                  {currentStock} {product.unit || 'units'}
                </span>
              </div>
            </div>

            {/* New Quantity Input */}
            <div>
              <label className="block text-sm font-medium text-primary-black mb-2">
                New Quantity <span className="text-red-500">*</span>
              </label>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => quickAdjust(-10)}
                  className="p-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-colors"
                >
                  <Minus className="w-4 h-4" />
                  10
                </button>
                <button
                  type="button"
                  onClick={() => quickAdjust(-1)}
                  className="p-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors"
                >
                  <Minus className="w-4 h-4" />
                </button>
                <input
                  type="number"
                  value={newQuantity}
                  onChange={(e) => setNewQuantity(Math.max(0, parseInt(e.target.value) || 0))}
                  min="0"
                  className="flex-1 px-4 py-2 text-center text-xl font-bold border border-secondary-lightGray rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-gold"
                />
                <button
                  type="button"
                  onClick={() => quickAdjust(1)}
                  className="p-2 bg-green-50 text-green-600 rounded-lg hover:bg-green-100 transition-colors"
                >
                  <Plus className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => quickAdjust(10)}
                  className="p-2 bg-green-100 text-green-600 rounded-lg hover:bg-green-200 transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  10
                </button>
              </div>
            </div>

            {/* Difference Display */}
            {difference !== 0 && (
              <div className={`p-3 rounded-lg ${difference > 0 ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                <div className="flex items-center gap-2">
                  {difference > 0 ? (
                    <Plus className="w-4 h-4 text-green-600" />
                  ) : (
                    <Minus className="w-4 h-4 text-red-600" />
                  )}
                  <span className={difference > 0 ? 'text-green-700' : 'text-red-700'}>
                    {difference > 0 ? `+${difference}` : difference} {product.unit || 'units'} change
                  </span>
                </div>
              </div>
            )}

            {/* Reason */}
            <div>
              <label className="block text-sm font-medium text-primary-black mb-2">
                Reason <span className="text-red-500">*</span>
              </label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={3}
                placeholder="Explain why this adjustment is needed..."
                className="w-full px-4 py-2 border border-secondary-lightGray rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-gold resize-none"
              />
              <p className="mt-1 text-xs text-secondary-gray">
                This will be recorded in the stock activity log
              </p>
            </div>

            {/* Warning */}
            <div className="flex items-start gap-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
              <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-yellow-700">
                This action will directly modify the stock level and create an audit record.
              </p>
            </div>

            {/* Actions */}
            <div className="flex gap-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-4 py-2 bg-secondary-offWhite text-primary-black font-semibold rounded-lg hover:bg-secondary-lightGray transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading || difference === 0 || (!isSingleBranch && canSelectBranch && !selectedBranchId)}
                className="flex-1 px-4 py-2 bg-accent-gold text-primary-black font-semibold rounded-lg hover:bg-accent-gold/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Adjusting...' : 'Confirm Adjustment'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default StockAdjustmentModal;

